import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { format } from 'date-fns';
import { X, Search, Calendar } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getDisplayUrl } from '../lib/r2Storage';
import toast from 'react-hot-toast';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectDate: (date: Date) => void;
}

export default function SearchModal({ isOpen, onClose, onSelectDate }: SearchModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  async function handleSearch() {
    if (!searchTerm.trim()) return;

    try {
      setLoading(true);

      // Search entries by delivery note and notes
      const { data: entriesData, error: entriesError } = await supabase
        .from('entries')
        .select(`
          id,
          date,
          delivery_note_number,
          notes,
          image_urls,
          farmer:farmers(name),
          customer:customers(name),
          products(id, description, crates, pallets)
        `)
        .or(`delivery_note_number.ilike.%${searchTerm}%,notes.ilike.%${searchTerm}%`)
        .order('date', { ascending: false });

      if (entriesError) throw entriesError;

      // Search entries by product description
      const { data: productData, error: productError } = await supabase
        .from('entries')
        .select(`
          id,
          date,
          delivery_note_number,
          notes,
          image_urls,
          farmer:farmers(name),
          customer:customers(name),
          products!inner(id, description, crates, pallets)
        `)
        .textSearch('products.description', searchTerm)
        .order('date', { ascending: false });

      if (productError) throw productError;

      // Search by numeric values (crates/pallets)
      const numericSearch = parseFloat(searchTerm);
      let quantityData: any[] = [];
      
      if (!isNaN(numericSearch)) {
        const { data: qData, error: qError } = await supabase
          .from('entries')
          .select(`
            id,
            date,
            delivery_note_number,
            notes,
            image_urls,
            farmer:farmers(name),
            customer:customers(name),
            products!inner(id, description, crates, pallets)
          `)
          .or(`products.crates.eq.${numericSearch},products.pallets.eq.${numericSearch}`)
          .order('date', { ascending: false });

        if (!qError && qData) {
          quantityData = qData;
        }
      }

      // Combine and deduplicate results
      const allResults = [...(entriesData || []), ...(productData || []), ...quantityData];
      const uniqueResults = Array.from(
        new Map(allResults.map(item => [item.id, item])).values()
      );

      setResults(uniqueResults);
    } catch (error) {
      console.error('Error searching entries:', error);
      toast.error('Failed to search entries');
    } finally {
      setLoading(false);
    }
  }

  function handleKeyPress(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="min-h-screen px-4 text-center">
        <Dialog.Overlay className="fixed inset-0 bg-black/30" />

        <span className="inline-block h-screen align-middle" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block w-full sm:max-w-2xl p-4 sm:p-6 my-0 sm:my-8 text-left align-middle transition-all transform bg-white shadow-xl h-screen sm:h-auto overflow-y-auto rounded-none sm:rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              Search Entries
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="relative mb-6">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Search by delivery note, products, quantities, or notes..."
              className="block w-full pl-10 pr-24 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              autoFocus
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <button
              onClick={handleSearch}
              disabled={loading || !searchTerm.trim()}
              className="absolute inset-y-0 right-0 px-4 py-2 border border-transparent text-sm font-medium rounded-r-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {results.map((entry) => (
              <div
                key={entry.id}
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition duration-150 ease-in-out"
                onClick={() => {
                  onSelectDate(new Date(entry.date));
                  onClose();
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">
                      {entry.delivery_note_number ? 
                        `Delivery Note: ${entry.delivery_note_number}` : 
                        'No Delivery Note'
                      }
                    </h3>
                    <div className="mt-1 text-sm text-gray-500 space-y-1">
                      <p>Farmer: {entry.farmer?.name}</p>
                      <p>Customer: {entry.customer?.name}</p>
                      {entry.products && entry.products.length > 0 && (
                        <div className="mt-2">
                          <p className="font-medium">Products:</p>
                          <ul className="list-disc list-inside">
                            {entry.products.map((product: any) => (
                              <li key={product.id} className="text-sm">
                                {product.description} - {product.crates} crates
                                {product.pallets > 0 && `, ${product.pallets} pallets`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {entry.notes && (
                        <div className="mt-2 text-sm text-gray-600 italic">
                          Notes: {entry.notes}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="ml-4 flex-shrink-0">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      {format(new Date(entry.date), 'MMM d, yyyy')}
                    </div>
                    {entry.image_urls && entry.image_urls.length > 0 && (
                      <img
                        src={getDisplayUrl(entry.image_urls[0])}
                        alt="Entry"
                        className="mt-2 w-20 h-20 object-cover rounded-md"
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
            {results.length === 0 && searchTerm && !loading && (
              <div className="text-center py-8 text-gray-500">
                No entries found matching your search
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}